import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputNumber } from 'primereact/inputnumber'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../contexts/toast'
import { TermOfPayment } from '../../../../../data/models'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config/context/useConfigContext'
import { requestPutTermsOfPaymentById } from '../../../data'
import { useInvoiceTermOfPaymentById } from '../../../data/hooks'

type Props = {
  invoiceId: string
  id: string
  selectedItems: TermOfPayment[]
  setSelectedItems: (params: any) => void
  setIsVisibleDeleteDialog: (params: any) => void
}

type FormValues = {
  days: number
  percent: number
}
type FormFieldName = keyof FormValues

export const EditTermsOfPayment: React.FC<Props> = ({
  invoiceId,
  id,
  selectedItems,
  setSelectedItems,
  setIsVisibleDeleteDialog,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()

  const { data: invoiceTermOfPayment, isLoading } = useInvoiceTermOfPaymentById(
    invoiceId,
    id,
  )

  const [visible, setVisible] = useState(false)
  const [isRequestRunning, setIsRequestRunning] = useState(false)

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      days: invoiceTermOfPayment?.Days!,
      percent: invoiceTermOfPayment?.Percent!,
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'days',
          config: config.invoice.editTermsOfPayment.days,
        },
        {
          fieldName: 'percent',
          config: config.invoice.editTermsOfPayment.percent,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // mutate()

        // deselected items
        setSelectedItems([])

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'invoice.termsOfPayments.table.edit.dialog.save.messages.success.summary',
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // submit form
  const submitForm = async (formData: FormValues) => {
    const newDto: TermOfPayment = produce(invoiceTermOfPayment!, draft => {
      draft.Days = formData.days
      draft.Percent = formData.percent
    })

    await requestPutTermsOfPaymentById(invoiceId, id, newDto)
  }

  // handleReject
  const handleDelete = () => {
    setSelectedItems([invoiceTermOfPayment])

    // open the delete dialog
    setIsVisibleDeleteDialog(true)

    // close the edit dialog
    setVisible(false)
  }

  // handleClose
  const handleClose = () => {
    formik.resetForm()
    setVisible(false)
  }

  // ************************************************************
  // components
  // ************************************************************
  // days
  const daysLabel = (
    <label
      htmlFor="days"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('days'),
      })}
    ></label>
  )

  const daysComponent = (
    <InputNumber
      id="days"
      value={formik.values.days}
      onChange={e => formik.setFieldValue('days', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('days'),
      })}
      min={0}
      locale={language}
      showButtons
    />
  )

  const daysErrorMsg = <div>{getFormErrorMessage('days')}</div>

  const daysWrapper = (
    <ConfigurableComponent
      config={config.invoice.editTermsOfPayment}
      component={daysComponent}
      label={daysLabel}
      errorMsg={daysErrorMsg}
      isLoading={isLoading}
    />
  )

  // percent
  const percentLabel = (
    <label
      htmlFor="percent"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('percent'),
      })}
    ></label>
  )

  const percentComponent = (
    <InputNumber
      id="percent"
      value={formik.values.percent}
      onChange={e => formik.setFieldValue('percent', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('percent'),
      })}
      min={0}
      suffix=" %"
      minFractionDigits={2}
      locale={language}
      showButtons
    />
  )

  const percentErrorMsg = <div>{getFormErrorMessage('percent')}</div>

  const percentWrapper = (
    <ConfigurableComponent
      config={config.invoice.editTermsOfPayment}
      component={percentComponent}
      label={percentLabel}
      errorMsg={percentErrorMsg}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <div className="flex align-content-center flex-wrap">
      <span className="mr-3">{t('common.titles.editTermsOfPayment')}</span>
    </div>
  )

  // footer
  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      <Button
        className="p-button-danger p-button-text w-full sm:w-auto mb-3 sm:mb-0"
        label={t('common.buttons.delete')}
        icon="pi pi-trash"
        onClick={handleDelete}
        type="reset"
      />
      <Button
        className="p-button-text ml-auto w-full sm:w-auto mb-3 sm:mb-0"
        label={t('common.buttons.cancel')}
        icon="pi pi-times"
        onClick={handleClose}
        type="button"
      />
      <Button
        label={t('common.buttons.save')}
        className="w-full sm:w-auto"
        icon={'pi pi-check'}
        type="submit"
        onClick={formik.submitForm}
        loading={isRequestRunning}
        disabled={!formik.dirty}
      />
    </div>
  )

  return (
    <>
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text"
        onClick={() => {
          setVisible(true)
        }}
        visible={config.invoice.editTermsOfPayment.enabled !== false}
      />

      <Dialog
        header={header}
        visible={visible}
        style={{ width: '35rem' }}
        breakpoints={{ [calculateBreakpointWidth(35)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12">{daysWrapper}</div>
          <div className="col-12">{percentWrapper}</div>
        </div>
      </Dialog>
    </>
  )
}
